import React from 'react';
import { Chip, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import { StyledTextField, StyledSelect, inputBorderStyles, labelStyle, ewsAutocompletePaperSx } from './SharedComponents';
import { CreateNewHandlerFormProps } from './types';
import { HANDLER_VERSION_OPTIONS, HANDLER_EXECUTOR_OPTIONS, DATA_SOURCE_OPTIONS, METRIC_KPI_OPTIONS } from './constants';
import styles from './CreateNewHandlerForm.module.scss';

const FIELD_HEIGHT = 50;
// Passed as a string (not a number) because MUI's sx prop multiplies numeric
// borderRadius values by theme.shape.borderRadius (4 by default) — a plain "4"
// silently became 16px and produced the pill-shaped corners.
const FIELD_RADIUS = '4px';
const FIELD_BORDER_COLOR = '#e3e7ed';
const FIELD_BG_COLOR = '#ffffff';
const THIN_BORDER_SX = { '& .MuiOutlinedInput-notchedOutline': { borderWidth: '0.8px !important' } };

const CreateNewHandlerForm: React.FC<CreateNewHandlerFormProps> = ({ form, errors, handlerTypeOptions, onFieldChange }) => {
    const selectedDataSources = DATA_SOURCE_OPTIONS.filter((opt) => form.dataSources.includes(opt.value));

    return (
        <div className={styles.form}>
            <div className={styles.row2}>
                <StyledTextField
                    label="Handler Name"
                    required
                    placeholder="e.g. DataOps RCA Agent"
                    value={form.handlerName}
                    onChange={(e) => onFieldChange('handlerName', e.target.value)}
                    error={!!errors.handlerName}
                    helperText={errors.handlerName}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
                <StyledSelect
                    label="Type"
                    required
                    value={form.handlerType}
                    onChange={(val) => onFieldChange('handlerType', val)}
                    options={handlerTypeOptions}
                    error={!!errors.handlerType}
                    helperText={errors.handlerType}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
            </div>

            <div className={styles.row3}>
                <StyledSelect
                    label="Version"
                    value={form.version}
                    onChange={(val) => onFieldChange('version', val)}
                    options={HANDLER_VERSION_OPTIONS}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
                <StyledSelect
                    label="Executor"
                    value={form.executor}
                    onChange={(val) => onFieldChange('executor', val)}
                    options={HANDLER_EXECUTOR_OPTIONS}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
                <StyledTextField
                    label="Timeout"
                    placeholder="e.g. 30"
                    value={form.timeout}
                    onChange={(e) => onFieldChange('timeout', e.target.value.replace(/[^0-9]/g, ''))}
                    inputProps={{ inputMode: 'numeric' }}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
            </div>

            <div className={styles.row2}>
                <CustomAutocomplete
                    multiple
                    fullWidth
                    size="small"
                    options={DATA_SOURCE_OPTIONS}
                    value={selectedDataSources}
                    componentsProps={{
                        paper: { sx: ewsAutocompletePaperSx }
                    }}
                    getOptionLabel={(opt: { label: string }) => opt.label}
                    isOptionEqualToValue={(opt: { value: string }, val: { value: string }) => opt.value === val.value}
                    onChange={(_e, newValue: { value: string; label: string }[]) => {
                        onFieldChange('dataSources', newValue.map((v) => v.value));
                    }}
                    renderTags={(tagValue: { value: string; label: string }[], getTagProps) =>
                        tagValue.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={option.label}
                                    deleteIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
                                    {...tagProps}
                                    sx={{
                                        backgroundColor: 'rgba(57, 172, 231, 0.16) !important',
                                        color: '#0B4F73 !important',
                                        border: '1px solid rgba(57, 172, 231, 0.45)',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: '24px',
                                        borderRadius: '16px',
                                        px: '7px',
                                        margin: '2px !important',
                                        maxWidth: '220px',
                                        '& .MuiChip-label': {
                                            color: '#0B4F73 !important',
                                            padding: '0 4px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        },
                                        '& .MuiChip-deleteIcon': {
                                            margin: 0,
                                            color: '#0B4F73 !important',
                                            '&:hover': { color: '#003771 !important' }
                                        }
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Data Source(s)"
                            size="small"
                            fullWidth
                            InputLabelProps={{ ...params.InputLabelProps, shrink: true, sx: labelStyle }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: FIELD_BG_COLOR,
                                    ...inputBorderStyles,
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: `${FIELD_BORDER_COLOR} !important`,
                                        borderWidth: '0.8px !important'
                                    },
                                    borderRadius: FIELD_RADIUS,
                                    minHeight: `${FIELD_HEIGHT}px`,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '4px',
                                    padding: '4px 8px !important'
                                },
                                '& .MuiAutocomplete-tag': {
                                    backgroundColor: 'rgba(57, 172, 231, 0.16) !important',
                                    color: '#0B4F73 !important',
                                    margin: '2px !important'
                                }
                            }}
                        />
                    )}
                />
                <StyledSelect
                    label="Output Metric/KPI"
                    value={form.outputMetric}
                    onChange={(val) => onFieldChange('outputMetric', val)}
                    options={METRIC_KPI_OPTIONS}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
            </div>

            <div className={styles.rowFull}>
                <StyledTextField
                    label="Description"
                    placeholder="e.g. Root cause analysis handler"
                    value={form.description}
                    onChange={(e) => onFieldChange('description', e.target.value)}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
            </div>

            <div className={styles.rowFull}>
                <StyledTextField
                    label="Tags"
                    placeholder="e.g. rca, critical, batch"
                    value={form.tags}
                    onChange={(e) => onFieldChange('tags', e.target.value)}
                    bgColor={FIELD_BG_COLOR}
                    height={FIELD_HEIGHT}
                    borderRadius={FIELD_RADIUS}
                    borderColor={FIELD_BORDER_COLOR}
                    sx={THIN_BORDER_SX}
                />
            </div>
        </div>
    );
};

export default CreateNewHandlerForm;
